import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import {
  ServiceSchema,
  type CreateServiceRequest,
  type Service,
} from '@repo/api-client/dtos';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postJSON } from '../internal/http';

export function useCreateService() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: (body: CreateServiceRequest) => {
      if (!orgId) throw new Error('No active organization');
      return postJSON(http, `/orgs/${orgId}/services`, body, ServiceSchema);
    },
    onSuccess: (service) => {
      queryClient.setQueryData(
        queryKeys.items.detail(service.orgId, service.id),
        service,
      );
      // List cache is `InfiniteData<{ items, nextCursor? }>` since
      // the B1 (2026-05-09) migration to `useInfiniteQuery`.
      type ServicesPage = { items: Service[]; nextCursor?: string };
      type ServicesCache = { pages: ServicesPage[]; pageParams: unknown[] };
      queryClient.setQueryData<ServicesCache>(
        queryKeys.items.all(service.orgId),
        (cache) => {
          if (!cache || !cache.pages || cache.pages.length === 0) {
            return { pages: [{ items: [service] }], pageParams: [undefined] };
          }
          const [first, ...rest] = cache.pages;
          return {
            ...cache,
            pages: [
              { ...first!, items: [service, ...first!.items] },
              ...rest,
            ],
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.items.all(service.orgId) });
    },
  });

  return {
    createService: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
